'use client';

import { useCallback, useEffect, useRef } from 'react';

import { Currency, getLocale, Locale } from '@libs/core';

import { Events } from '../constants';
import { dataLayerPush, type Item } from '../gtm';

type UseViewItemListImpressionsParams = {
    /** Carousel-ordered items, one per rendered card. Each carries its own 1-based `index` and list values. */
    items: Item[];
};

/**
 * Time window (ms) opened by the first newly visible card. Every card that becomes visible within it
 * joins the same `view_item_list` event. This groups the staggered visibility crossings produced while
 * the carousel animates an arrow scroll (each card crosses the threshold on a different frame) into a
 * single event instead of one event per card.
 */
const IMPRESSION_BATCH_WINDOW_MS = 500;

const impressionId = (item: Item) => item.item_variant || item.item_id;

const listFingerprint = (items: Item[]) => items.map(impressionId).join(',');

const emitViewItemList = (visibleItems: Item[]) => {
    if (visibleItems.length === 0) return;

    const currency = getLocale() === Locale['en-US'] ? Currency.USD : Currency.CAD;

    dataLayerPush({ ecommerce: null });
    dataLayerPush({
        event: Events.VIEW_ITEM_LIST,
        ecommerce: {
            currency,
            category: null,
            sub_category1: null,
            sub_category2: null,
            items: visibleItems
                .slice()
                .sort((a, b) => a.index - b.index)
                .map(item => ({ ...item, item_search_term: null })),
        },
    });
};

/**
 * Batches `view_item_list` impressions for a product carousel.
 *
 * Cards call {@link onVisible} with their carousel index when they enter the viewport. Newly visible
 * cards are collected and flushed as a single `view_item_list` event per wave. Deduping is by product
 * identity (`item_variant`, then `item_id`) so a replacement list that reuses carousel indexes still
 * reports. History resets only when the rendered items change — not when the cart SKU list changes
 * while `placeholderData` is still showing the previous cards. An in-flight batch is flushed on that
 * change, and again on unmount, so a quick cart update, empty cart, login, or click-through to PDP
 * does not drop the list the shopper just saw.
 */
export const useViewItemListImpressions = ({ items }: UseViewItemListImpressionsParams) => {
    const seenRef = useRef<Set<string>>(new Set());
    const queueRef = useRef<Item[]>([]);
    const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
    const fingerprintRef = useRef(listFingerprint(items));
    const pendingFlushRef = useRef<Item[]>([]);
    const itemsRef = useRef(items);
    itemsRef.current = items;

    const fingerprint = listFingerprint(items);

    if (fingerprintRef.current !== fingerprint) {
        fingerprintRef.current = fingerprint;
        if (queueRef.current.length > 0) {
            pendingFlushRef.current = queueRef.current;
            queueRef.current = [];
        }
        seenRef.current = new Set();
        if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = undefined;
        }
    }

    const flush = useCallback(() => {
        timerRef.current = undefined;
        const queued = queueRef.current;
        queueRef.current = [];
        emitViewItemList(queued);
    }, []);

    const onVisible = useCallback(
        (index: number) => {
            const item = itemsRef.current[index];
            if (!item) return;

            const id = impressionId(item);
            if (!id || seenRef.current.has(id)) return;

            seenRef.current.add(id);
            queueRef.current.push(item);

            timerRef.current ??= setTimeout(flush, IMPRESSION_BATCH_WINDOW_MS);
        },
        [flush]
    );

    useEffect(() => {
        if (pendingFlushRef.current.length === 0) return;

        const leftover = pendingFlushRef.current;
        pendingFlushRef.current = [];
        emitViewItemList(leftover);
    }, [fingerprint]);

    useEffect(
        () => () => {
            if (timerRef.current) {
                clearTimeout(timerRef.current);
                timerRef.current = undefined;
            }

            const leftover = [...pendingFlushRef.current, ...queueRef.current];
            pendingFlushRef.current = [];
            queueRef.current = [];
            emitViewItemList(leftover);
        },
        []
    );

    return { onVisible };
};
